import { secondsToMilliseconds } from "@suse-o11y/date-time";
import { identity } from "@suse-o11y/identity";
import { ImmutableMap } from "@suse-o11y/immutable-map";
import { prop } from "@suse-o11y/prop";
import { useMemo } from "react";

import { type NodeId, type QueryView, Type, type Version, type ViewFlagValue } from "~/models/dto";

import type { Api } from "../common/openapi";
import { getViewsFn } from "../common/openapi/api";
import { useQuery } from "../queries/react-query";

import { useUserProfile } from "./use-user-profile";

const toQueryView = (view: Api.View): QueryView => {
	const { topologyQuery, ...viewWithoutTopologyQuery } = view;

	return {
		...viewWithoutTopologyQuery,
		_type: Type.QueryView,
		id: view.id as NodeId,
		query: topologyQuery,
		queryVersion: "0.0.1" as Version,
		flags: view.flags as unknown as Array<ViewFlagValue>,
	};
};

export const queryViews = {
	queryKey: ["queryViews"] as const,
	queryFn: () => getViewsFn({}).then((result) => result.views),
	staleTime: secondsToMilliseconds(30),
	cacheTime: Number.POSITIVE_INFINITY,
};

/**
 * Hook to access query views from the backend, filtered by user profile settings.
 *
 * @return a map of query views, or undefined if the query views are not yet loaded
 */
export function useQueryViews(): undefined | ImmutableMap<NodeId, QueryView>;

/**
 * Hook to access query views from the backend, filtered by user profile settings, with a selector to transform the resulting views.
 *
 * @param select optional selector to transform the resulting views
 *
 * @return the selected result of the selector function, or undefined if the query views are not yet loaded
 */
export function useQueryViews<TResult>(
	select: (views: ImmutableMap<NodeId, QueryView>) => TResult,
): undefined | TResult;

export function useQueryViews<TResult>(
	select: (views: ImmutableMap<NodeId, QueryView>) => TResult = identity as typeof select,
): undefined | TResult {
	const [hideUnavailableConnections] = useUserProfile({
		select: prop("hideUnavailableConnections"),
	});

	const { data: apiViews } = useQuery(queryViews);

	const normalizedViews = useMemo(() => {
		if (apiViews == null || hideUnavailableConnections == null) {
			return undefined;
		}

		return ImmutableMap.fromList((view) => view.id as NodeId, apiViews).mapValues((view) => ({
			...toQueryView(view),
			...(hideUnavailableConnections && { connectedComponents: false }),
		}));
	}, [apiViews, hideUnavailableConnections]);

	return useMemo(
		() => (normalizedViews == null ? normalizedViews : select(normalizedViews)),
		[select, normalizedViews],
	);
}
